// BUILD-046 display-only cleanup, cycle D1: how a person's name is DRAWN. What the name says (short form, full name, the test marker) is displayNames.ts's;
// this file draws it and gives the full name to the people who need it.
//
//   PersonName   the short name ("Alice", "Alex S.") with the caller tag after it ("Alice (you)") when it is the caller. When the short form differs from the full
//                name the full name is on HOVER (a tooltip that opens at once, so the browser's slower `title` bubble is withdrawn while it shows) and on KEYBOARD FOCUS
//                (the same tooltip, `role="tooltip"`, referenced by `aria-describedby` while it is shown), and Escape dismisses it. The element is focusable (tabIndex 0) only
//                when `focusable` is true (the default): use `focusable={false}` where the surrounding control is already interactive, and put the full name in that control's
//                accessible name or title instead.
//   NameTipText  the same for any text that stands for people ("Alice, Ben, Cara"): `text` is shown, `fullText` is the tooltip.
//   useNameTip   the hook behind both, for a host that already owns the focus (a chip with its Remove button): the host spreads `handlers` on its element, sets the
//                anchor and renders `overlay`; the tooltip then opens when the pointer is over the host or focus is anywhere inside it.
//
// The tooltip is drawn through a portal into <body> with fixed coordinates (the pattern of RequesterBadge.tsx), so a table's overflow-hidden can never clip it; it stays
// inside the viewport and flips above the name near the bottom. Escape is caught in the capture phase while a tooltip is open, so the first Escape closes the tooltip
// and the second one the dialog the name sits in (WCAG 1.4.13, dismissible).
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, FocusEventHandler, MouseEventHandler, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { personDisplay } from './displayNames';
import type { PersonExtra, PersonInput } from './displayNames';

const VIEWPORT_MARGIN = 8;

interface TipPosition {
  top: number;
  left: number;
  above: boolean;
}

export interface NameTipHandlers {
  onMouseEnter: MouseEventHandler<HTMLElement>;
  onMouseLeave: MouseEventHandler<HTMLElement>;
  onFocus: FocusEventHandler<HTMLElement>;
  onBlur: FocusEventHandler<HTMLElement>;
}

export interface NameTip {
  /** The tooltip is showing now. */
  open: boolean;
  /** The id of the tooltip element (for `aria-describedby` while `open`). */
  tipId: string;
  /** Marks the element the tooltip is placed against. */
  setAnchor: (element: HTMLElement | null) => void;
  /** Spread on the element that owns the hover and the focus. */
  handlers: NameTipHandlers;
  /** The tooltip itself (a portal), or null while it is closed. Render it anywhere in the component. */
  overlay: ReactNode;
}

/** The tooltip state of one name. `text` is what the tooltip says; null or '' means there is nothing to add and the tooltip never opens. */
export function useNameTip(text: string | null): NameTip {
  const tipId = useId();
  const anchorRef = useRef<HTMLElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const [overAnchor, setOverAnchor] = useState(false);
  const [overTip, setOverTip] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [position, setPosition] = useState<TipPosition | null>(null);
  const enabled = typeof text === 'string' && text !== '';
  const open = enabled && (overAnchor || overTip || focused) && !dismissed;

  function place(): void {
    const anchor = anchorRef.current;
    const tip = tipRef.current;
    if (anchor === null || tip === null) return;
    const rect = anchor.getBoundingClientRect();
    const width = tip.offsetWidth;
    const height = tip.offsetHeight;
    const left = Math.max(VIEWPORT_MARGIN, Math.min(rect.left, window.innerWidth - width - VIEWPORT_MARGIN));
    const above = rect.bottom + height > window.innerHeight - VIEWPORT_MARGIN && rect.top - height > VIEWPORT_MARGIN;
    setPosition({ top: above ? rect.top - height : rect.bottom, left, above });
  }

  // Measured before paint, so the tooltip never flashes at the wrong place.
  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, text]);

  // Escape closes the tooltip however it was opened. It is caught in the capture phase so that, inside a dialog, the first Escape only closes the tooltip.
  useEffect(() => {
    if (!open) return undefined;
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setDismissed(true);
    }
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open]);

  const handlers: NameTipHandlers = {
    onMouseEnter: () => {
      setOverAnchor(true);
      setDismissed(false);
    },
    onMouseLeave: () => {
      setOverAnchor(false);
      setDismissed(false);
    },
    onFocus: () => {
      setFocused(true);
      setDismissed(false);
    },
    onBlur: () => {
      setFocused(false);
      setDismissed(false);
    },
  };

  const style: CSSProperties = position === null ? { position: 'fixed', top: 0, left: 0, visibility: 'hidden' } : { position: 'fixed', top: position.top, left: position.left };
  const overlay: ReactNode = open
    ? createPortal(
        <div ref={tipRef} style={style} className={`z-[60] w-max max-w-sm ${position?.above === true ? 'pb-1' : 'pt-1'}`} onMouseEnter={() => setOverTip(true)} onMouseLeave={() => setOverTip(false)}>
          <div id={tipId} role="tooltip" data-testid="person-tooltip" className="rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-normal normal-case leading-snug tracking-normal text-white shadow-lg">
            {text}
          </div>
        </div>,
        document.body
      )
    : null;

  return {
    open,
    tipId,
    setAnchor: (element) => {
      anchorRef.current = element;
    },
    handlers,
    overlay,
  };
}

const FOCUS_RING = 'rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]';

export interface NameTipTextProps {
  /** What is shown (already in its short form). */
  text: string;
  /** What the tooltip says; null, '' or the same as `text` shows `text` plainly with no tooltip. */
  fullText: string | null;
  /** Reachable by Tab (default). Pass false where the surrounding control is already interactive. */
  focusable?: boolean;
  className?: string;
}

/** Text that stands for people, with the full names on hover and focus when they add something. */
export function NameTipText({ text, fullText, focusable = true, className }: NameTipTextProps) {
  const different = typeof fullText === 'string' && fullText !== '' && fullText !== text;
  const tip = useNameTip(different ? fullText : null);
  if (!different) return <span className={className}>{text}</span>;
  return (
    <>
      <span
        ref={tip.setAnchor}
        tabIndex={focusable ? 0 : undefined}
        // The browser's own bubble would show a second later, beside the tooltip that is already open: it is withdrawn while the tooltip shows.
        title={tip.open ? undefined : (fullText as string)}
        aria-describedby={tip.open ? tip.tipId : undefined}
        data-full-name={fullText as string}
        className={`${focusable ? FOCUS_RING : ''} ${className ?? ''}`.trim() || undefined}
        {...tip.handlers}
      >
        {text}
      </span>
      {tip.overlay}
    </>
  );
}

export interface PersonNameProps {
  /** A display string or an object that may carry structured name fields (displayNames.personDisplay). */
  person: PersonInput;
  /** The department / role of the person when `person` is only a display string. */
  extra?: PersonExtra;
  /** Appends "(you)" after the short form (and after the full name in the tooltip). */
  isCaller?: boolean;
  focusable?: boolean;
  className?: string;
}

/** One person: the short name, with the full name on hover and keyboard focus. */
export function PersonName({ person, extra, isCaller = false, focusable = true, className }: PersonNameProps) {
  const display = personDisplay(person, extra);
  if (display.short === '') return null;
  const tag = isCaller ? ' (you)' : '';
  return <NameTipText text={`${display.short}${tag}`} fullText={display.full === display.short ? null : `${display.full}${tag}`} focusable={focusable} className={className} />;
}
